import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  LayoutDashboard,
  Sparkles,
  Users,
  Package,
  Activity,
  Box,
  Code2,
  BarChart3,
  Settings,
  Building,
  Key,
  Bell,
  Shield,
  HelpCircle,
  User,
  Search,
  Command,
  BookOpen
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { searchItems, SearchItem } from '@/lib/search-index'
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'

// Icon mapping
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  Sparkles,
  Users,
  Package,
  Activity,
  Box,
  Code2,
  BarChart3,
  Settings,
  Building,
  Key,
  Bell,
  Shield,
  HelpCircle,
  User,
  BookOpen,
}

interface CommandPaletteProps {
  enabledFeatures?: string[]
}

export function CommandPalette({ enabledFeatures = [] }: CommandPaletteProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchItem[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const { orgId } = useParams<{ orgId: string }>()

  // Handle keyboard shortcut to open
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K or Ctrl+K to open
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen(true)
      }
      // Escape to close
      if (e.key === 'Escape' && open) {
        setOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open])

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0)
    } else {
      setQuery('')
      setResults([])
      setSelectedIndex(0)
    }
  }, [open])

  // Search on query change
  useEffect(() => {
    const searchResults = searchItems(query, enabledFeatures)
    setResults(searchResults)
    setSelectedIndex(0)
  }, [query, enabledFeatures])

  // Handle navigation
  const handleSelect = useCallback((item: SearchItem) => {
    if (orgId) {
      navigate(`/${orgId}${item.url}`)
    }
    setOpen(false)
  }, [navigate, orgId])

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(i => Math.min(i + 1, results.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(i => Math.max(i - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (results[selectedIndex]) {
          handleSelect(results[selectedIndex])
        }
        break
    }
  }

  const getIcon = (iconName?: string) => {
    if (!iconName) return Search
    return iconMap[iconName] || Search
  }

  return (
    <>
      {/* Search trigger button */}
      <button
        onClick={() => setOpen(true)}
        className="relative flex items-center w-64 pl-10 pr-4 py-2 text-sm bg-muted rounded-md border-0 focus:ring-2 focus:ring-primary focus:outline-none text-muted-foreground hover:bg-muted/80 transition-colors"
      >
        <Search className="absolute left-3 h-4 w-4 text-muted-foreground" />
        <span className="flex-1 text-left">Search...</span>
        <kbd className="hidden sm:inline-flex items-center gap-1 rounded border bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          <Command className="h-3 w-3" />K
        </kbd>
      </button>

      {/* Command palette dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="overflow-hidden p-0 max-w-lg">
          <div className="flex flex-col">
            {/* Search input */}
            <div className="flex items-center border-b px-3">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search pages and settings..."
                className="flex h-12 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            {/* Results */}
            <div className="max-h-[300px] overflow-y-auto">
              {query && results.length === 0 && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  No results found for "{query}"
                </div>
              )}
              
              {results.length > 0 && (
                <div className="p-2">
                  {results.map((item, index) => {
                    const Icon = getIcon(item.icon)
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleSelect(item)}
                        className={cn(
                          "relative flex w-full cursor-pointer select-none items-start rounded-sm px-3 py-2.5 text-sm outline-none transition-colors gap-3",
                          index === selectedIndex 
                            ? "bg-accent text-accent-foreground" 
                            : "hover:bg-accent/50"
                        )}
                      >
                        <Icon className="h-5 w-5 shrink-0 opacity-70 mt-0.5" />
                        <div className="flex flex-col items-start flex-1 min-w-0">
                          <div className="flex items-center gap-2 w-full">
                            <span className="font-medium">{item.title}</span>
                            {item.section && (
                              <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                {item.section}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                            {item.description}
                          </span>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
              
              {!query && (
                <div className="p-4 text-sm text-muted-foreground">
                  <p className="mb-2">Quick actions:</p>
                  <div className="space-y-1">
                    <p className="flex items-center gap-2">
                      <kbd className="rounded border bg-background px-1.5 py-0.5 text-xs">↑↓</kbd>
                      Navigate
                    </p>
                    <p className="flex items-center gap-2">
                      <kbd className="rounded border bg-background px-1.5 py-0.5 text-xs">Enter</kbd>
                      Select
                    </p>
                    <p className="flex items-center gap-2">
                      <kbd className="rounded border bg-background px-1.5 py-0.5 text-xs">Esc</kbd>
                      Close
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
